import RecoverableImage from '../../Media/RecoverableImage';
import { mediaImageSources, messageImageRefresh } from '../../../util/imageRecovery';
import { decodeDisplayText } from '../../../util/displayText';
import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { cloudinaryCore, isCloudinaryMedia } from '../../../util/cloudinary';
import {
    GREEN,
    PALE_WHITE_1,
    PALE_WHITE_6,
    WHITE
} from '../../../appearance/Colors';

import styled from 'styled-components';
import { standardMenuSurface } from '../../../appearance/menuStyles';
import MessageColorPalette from '../MessageColorPalette';
import SimilarColorImages from '../SimilarColorImages';
import { Image, Transformation } from 'cloudinary-react';
import { FiCheck, FiDownload, FiEdit2, FiMoreVertical, FiTrash2 } from 'react-icons/fi';
import { LuScanText } from 'react-icons/lu';
import { IoIosRepeat } from 'react-icons/io';
import environment from '../../../env-config';
import { prettySize } from '../../../util/fileSizeCalculator';
import CarouselModal from '../../../components/Modals/CarouselModal';
import DeleteModal from '../../../components/Modals/DeleteModal';
import RenameFileModal from '../../../components/Modals/RenameFileModal';

import FileSaver from 'file-saver';

import useCloudinaryRequest from '../../../hooks/useCloudinaryRequest';
import getImagePreviews from '../../../util/getImagePreviews';

import useUpdateMessageMutation from '../../../hooks/useUpdateMessageMutation';

import { useQuery } from '@apollo/client';
import { QUERY } from '../../../gql-operations';
import { downloadBlobInBrowser, isTauriEnvironment, saveBlobWithTauri } from '../../../util/downloadHelpers';

import useGetMaxFileSize from '../../../util/max_file_size';
import { AuthContext } from '../../../util/context';
import customAnalytics from '../../../util/custom-analytics';
import { isFileTypeImage } from '../../../constants/fileUtils';

function ImagePreviewPlugin(props) {
    return props.readOnly ? <ImagePreviewPluginContent {...props} /> : <EditableImagePreviewPlugin {...props} />;
}

function EditableImagePreviewPlugin(props) {
    const { user_max_file_size } = useGetMaxFileSize();

    const { updateMessageImages } = useCloudinaryRequest();
    const { deleteImageMutation, updateFiles } = useUpdateMessageMutation();
    const { refetch: refetchDownloadFile } = useQuery(QUERY.DOWNLOAD_FILE, { skip: true });

    return <ImagePreviewPluginContent {...props} tools={{ user_max_file_size, updateMessageImages, deleteImageMutation, updateFiles, refetchDownloadFile }} />;
}

function ImagePreviewPluginContent({
    message,
    width,
    height,
    crop,
    onClick,
    style,
    dropzoneFiles,
    hideActions,
    hideAddButton,
    onClearPendingSelection,
    onOpenVideoWorkspace,
    readOnly = false,
    tools = {},
    ...rest
}) {
    const {t: lnT} = useTranslation();

    const isTauri = isTauriEnvironment();
    const wrapperRef = React.useRef();

    // 310 is the widht of the side panel
    const [wrapperWidth, setWrapperWidth] = useState(310);
    // const [wrapperHeight, setWrapperHeight] = useState(20);
    const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
    const [showImageActionMenu, setShowImageActionMenu] = useState(false);
    const [ocrCopied, setOcrCopied] = useState(false);

    const [imagesPreview, setImagesPreview] = useState(undefined);
    const resolvedOcrText = message?.image?.ocrText || message?.ocrText || null;


    const copyOcrText = async e => {
        e?.stopPropagation();
        const text = resolvedOcrText;
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setOcrCopied(true);
            setTimeout(() => setOcrCopied(false), 2000);
        } catch {
            setOcrCopied(false);
        }
    };

    const openSavedMedia = () => {
        const image = message?.image;
        const src = image?.thumbnail?.xl || image?.thumbnail?.lg || image?.thumbnail?.original || image?.cdn_url || image?.url;
        if (typeof onOpenVideoWorkspace === 'function' && src) {
            onOpenVideoWorkspace(message, {
                type: 'image',
                src,
                image,
                displayName: image?.displayName || image?.name,
                ocrText: resolvedOcrText || '',
                originalUrl: image?.thumbnail?.original || image?.url || image?.cdn_url
            });
        } else setLightboxIsOpen(true);
    };

    const inputFile = React.useRef(null);
    const actionMenuRef = React.useRef(null);

    const {
        state: { uploadProgress } = {},
        context: { setUploadProgress: setUploadProgressContext } = {}
    } = React.useContext(AuthContext) || {};

    const { user_max_file_size, updateMessageImages, deleteImageMutation, updateFiles, refetchDownloadFile } = tools;
    const onChangeFile = async event => {
        if (readOnly) return;
        event.stopPropagation();
        event.preventDefault();
        const fileListAsArray = Array.from(event.target.files);
        getPreviews(fileListAsArray);
        // this.setState({file}); /// if you want to upload latter
    };

    const onDropzoneFiles = async files => {
        if (readOnly) return;
        const fileListAsArray = Array.from(files);
        getPreviews(fileListAsArray);
    };

    useEffect(() => {
        if (!readOnly && dropzoneFiles) {
            onDropzoneFiles(dropzoneFiles);
        }
        return () => {
            // cleanup
        };
    }, [dropzoneFiles, readOnly]);

    const getPreviews = async files => {
        let res = await getImagePreviews(files);
        if (res) {
            setImagesPreview(res);
        }
    };

    const clearFileToSend = () => {
        setImagesPreview(undefined);
        if (onClearPendingSelection) {
            onClearPendingSelection();
        }
        // inputFile.current.value = '';
    };

    useEffect(() => {
        if (wrapperRef.current) {
            // console.log('wrapperRef', wrapperRef.current.offsetWidth);
            setWrapperWidth(wrapperRef.current.offsetWidth);
            // setWrapperHeight(wrapperRef.current.offsetHeight);
        }
    }, [wrapperRef]);

    useEffect(() => {
        if (!showImageActionMenu || typeof document === 'undefined') {
            return undefined;
        }

        const handlePointerDown = event => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setShowImageActionMenu(false);
            }
        };

        const handleEscape = event => {
            if (event.key === 'Escape') {
                setShowImageActionMenu(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showImageActionMenu]);

    useEffect(() => {
        setShowImageActionMenu(false);
    }, [message?.id, imagesPreview]);

    const onButtonClick = () => {
        inputFile.current.click();
    };

    const closeImageActionMenu = () => {
        setShowImageActionMenu(false);
    };

    const renameImage = async displayName => {
        if (readOnly) return;
        const updatedMessage = await updateFiles(message, {
            image: { ...message.image, displayName }
        });
        if (!updatedMessage) {
            throw new Error('Unable to rename the image.');
        }
        customAnalytics.message_edit({ action: 'Rename', type: 'Image' }, message);
        setIsRenameModalOpen(false);
    };

    const publishFiles = async index => {
        if (readOnly) return;
        let filesToSend = imagesPreview.map(previewItem => previewItem.file);
        let imageFiles = [];
        // let ordinaryFiles = [];
        filesToSend.forEach(file => {
            if (isFileTypeImage(file.type)) {
                imageFiles.push(file);
            }
            // else {
            //     ordinaryFiles.push(file);
            // }
        });
        // somewhere here the successful upload
        clearFileToSend();
        // if (ordinaryFiles.length) {
        //     await updateMessageFiles(ordinaryFiles, message);
        // }
        if (imageFiles.length) {
            const onlyFirst = imageFiles[index];
            await updateMessageImages([onlyFirst], message);
            customAnalytics.message_edit({
                "action": message.image ? "Replace" : "Add",
                "type": "Image"
            }, message);
        }
    };

    const deleteImage = async () => {
        if (readOnly) return;
        await deleteImageMutation(message);
    };

    // NOT USED NOW
    // const removeFromTheList = file => {
    //     let previewClone = [...imagesPreview];
    //     let previewIndex = previewClone.findIndex(listFile => listFile.file === file);

    //     if (previewIndex > -1) {
    //         previewClone.splice(previewIndex, 1);
    //         if (previewClone.length > 0) {
    //             setImagesPreview(previewClone);
    //         } else {
    //             setImagesPreview(undefined);
    //         }
    //     }
    // };

    const downloadImage = async (imageUrl, displayName, size, image) => {
        if (readOnly) {
            FileSaver.saveAs(image?.cdn_url || imageUrl, displayName || image?.name || 'image');
            return;
        }
        if (!setUploadProgressContext) {
            return;
        }
        
        const fileName = displayName || image?.name || 'image';
        const progressKey = `download_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const updateProgress = (percentage) => {
            if (!setUploadProgressContext) {
                return;
            }
            // setUploadProgressContext expects the data object directly, not a function
            // So we need to read current state and merge manually
            const currentProgress = uploadProgress || {};
            const newProgress = {
                ...currentProgress,
                [progressKey]: {
                    state: percentage < 100 ? 'pending' : 'done',
                    percentage,
                    type: 'image',
                    name: fileName
                }
            };
            setUploadProgressContext(newProgress);
        };
        
        try {
            updateProgress(20);
            // Force a small delay to ensure state update is visible
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const { url, public_id, name, cdn_url } = image || {};

            // Detect if this is a Cloudinary image
            // Cloudinary images have a public_id like "luckynote/abc123" - S3 images don't have one
            const isCloudinaryImage = isCloudinaryMedia(image);
            
            let downloadUrl;
            let fileDisplayName = displayName || name || 'image';
            
            if (isCloudinaryImage) {
                // For Cloudinary images, use Cloudinary URL (no refresh needed)
                updateProgress(40);
                downloadUrl = await cloudinaryCore.url(imageUrl || url);
            } else if (name) {
                // For S3 images, get fresh S3 signed URL using downloadFile query
                updateProgress(40);
                // Use image.name (S3 path) to get a fresh signed URL
                try {
                    const { data, error } = await refetchDownloadFile({
                        data: {
                            fileName: name  // S3 path
                        }
                    });
                    
                    if (error) {
                        throw error;
                    }
                    
                    if (data?.downloadFile?.__typename === 'S3File') {
                        downloadUrl = data.downloadFile.url;  // Fresh S3 signed URL
                    } else {
                        throw new Error('Failed to get download URL');
                    }
                } catch (refreshError) {
                    console.error('Error refreshing S3 URL, trying fallback:', refreshError);
                    // Fallback to thumbnail.original, CDN URL, or cached URL
                    downloadUrl = image?.thumbnail?.original || cdn_url || imageUrl || url;
                }
            } else {
                // No S3 path available, use cached URL
                downloadUrl = image?.thumbnail?.original || cdn_url || imageUrl || url;
            }
            
            // Fetch as blob to handle cross-origin downloads properly
            updateProgress(60);
            const response = await fetch(downloadUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            updateProgress(80);
            const blob = await response.blob();
            if (isTauri) {
                const saved = await saveBlobWithTauri(blob, fileDisplayName);
                if (!saved) {
                    const currentProgress = uploadProgress || {};
                    const { [progressKey]: removed, ...rest } = currentProgress;
                    setUploadProgressContext(Object.keys(rest).length > 0 ? rest : undefined);
                    return;
                }
            } else {
                downloadBlobInBrowser(blob, fileDisplayName);
            }
            
            // Mark as complete
            updateProgress(100);
            
            // Clear progress after a short delay
            setTimeout(() => {
                const currentProgress = uploadProgress || {};
                const { [progressKey]: removed, ...rest } = currentProgress;
                setUploadProgressContext(Object.keys(rest).length > 0 ? rest : undefined);
            }, 2000);
            
            customAnalytics.ui_action({
                'action': "Download Image",
                "size": size
            });
        } catch (error) {
            console.error('Error downloading image:', error);
            // Final fallback: try direct download link
            try {
                updateProgress(50);
                if (isCloudinaryMedia(image)) {
                    // Cloudinary fallback
                    const cloudUrl = await cloudinaryCore.url(imageUrl || image.url);
                    if (isTauri) {
                        const cloudResponse = await fetch(cloudUrl);
                        if (!cloudResponse.ok) {
                            throw new Error(`HTTP error! status: ${cloudResponse.status}`);
                        }
                        const cloudBlob = await cloudResponse.blob();
                        const saved = await saveBlobWithTauri(cloudBlob, displayName || image.name || 'image');
                        if (!saved) {
                            const currentProgress = uploadProgress || {};
                            const { [progressKey]: removed, ...rest } = currentProgress;
                            setUploadProgressContext(Object.keys(rest).length > 0 ? rest : undefined);
                            return;
                        }
                    } else {
                        FileSaver.saveAs(cloudUrl, displayName || image.name || 'image');
                    }
                    
                    // Mark as complete
                    updateProgress(100);
                    setTimeout(() => {
                        const currentProgress = uploadProgress || {};
                        const { [progressKey]: removed, ...rest } = currentProgress;
                        setUploadProgressContext(Object.keys(rest).length > 0 ? rest : undefined);
                    }, 2000);
                } else {
                    // S3 fallback
                    const fallbackUrl = image?.thumbnail?.original || image?.cdn_url || imageUrl || image?.url;
                    if (isTauri) {
                        const fallbackResponse = await fetch(fallbackUrl);
                        if (!fallbackResponse.ok) {
                            throw new Error(`HTTP error! status: ${fallbackResponse.status}`);
                        }
                        const fallbackBlob = await fallbackResponse.blob();
                        const saved = await saveBlobWithTauri(fallbackBlob, displayName || image?.name || 'image');
                        if (!saved) {
                            const currentProgress = uploadProgress || {};
                            const { [progressKey]: removed, ...rest } = currentProgress;
                            setUploadProgressContext(Object.keys(rest).length > 0 ? rest : undefined);
                            return;
                        }
                    } else {
                        const fallbackResponse = await fetch(fallbackUrl);
                        if (!fallbackResponse.ok) {
                            throw new Error(`HTTP error! status: ${fallbackResponse.status}`);
                        }
                        const fallbackBlob = await fallbackResponse.blob();
                        downloadBlobInBrowser(fallbackBlob, displayName || image?.name || 'image');
                    }
                    
                    // Mark as complete
                    updateProgress(100);
                    setTimeout(() => {
                        const currentProgress = uploadProgress || {};
                        const { [progressKey]: removed, ...rest } = currentProgress;
                        setUploadProgressContext(Object.keys(rest).length > 0 ? rest : undefined);
                    }, 2000);
                }
            } catch (fallbackError) {
                console.error('Fallback download also failed:', fallbackError);
                // Remove failed download from progress
                const currentProgress = uploadProgress || {};
                const { [progressKey]: removed, ...rest } = currentProgress;
                setUploadProgressContext(Object.keys(rest).length > 0 ? rest : undefined);
            }
        }
    };

    const renderPreview = () => {
        if (imagesPreview) {
            return imagesPreview.map(({ reader, file, isLargeFile }, index) => {
                const { name, size, type } = file;
                const hasPreview = reader?.result && type !== 'image/heic';
                return (
                    <ImageWrapper key={index} style={{ ...style }} onClick={() => hasPreview && setLightboxIsOpen(true)}>
                        <TopSection>
                            <MessageColorPalette message={message} />
                            <TopRightActions>
                                {renderImageMenuTrigger(
                                    <>
                                        {size > user_max_file_size ? (
                                            <>
                                                <ImageMenuItem $disabled>
                                                    <ImageMenuIcon>
                                                        <FiTrash2 size={16} />
                                                    </ImageMenuIcon>{lnT('messages:fileTooLarge')}</ImageMenuItem>
                                                <ImageMenuSeparator />
                                            </>
                                        ) : (
                                            <ImageMenuItem
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    closeImageActionMenu();
                                                    publishFiles(index);
                                                }}
                                            >
                                                <ImageMenuIcon>
                                                    <IoIosRepeat size={16} />
                                                </ImageMenuIcon>
                                                {message.image ? lnT('messages:replace') : lnT('messages:publish')}
                                            </ImageMenuItem>
                                        )}
                                        <ImageMenuSeparator />
                                        <ImageMenuItem
                                            onClick={e => {
                                                e.stopPropagation();
                                                closeImageActionMenu();
                                                clearFileToSend();
                                            }}
                                        >
                                            <ImageMenuIcon destructive>
                                                <FiTrash2 size={16} />
                                            </ImageMenuIcon>
                                            <ImageMenuDestructiveLabel>{lnT('common:cancel')}</ImageMenuDestructiveLabel>
                                        </ImageMenuItem>
                                        {renderImageInfoSection([
                                            { label: lnT('common:name'), value: decodeDisplayText(name) },
                                            { label: lnT('folders:size'), value: prettySize(size) },
                                            {
                                                label: lnT('messages:type_baaddf70'),
                                                value: type ? String(type).replace('image/', '').toUpperCase() : null
                                            }
                                        ])}
                                    </>
                                )}
                            </TopRightActions>
                        </TopSection>
                        <ImageStage ref={wrapperRef}>
                            {hasPreview ? (
                                <PreviewImage
                                    src={reader.result}
                                    style={{ height: height ? height : 300, objectFit: 'contain' }}
                                ></PreviewImage>
                            ) : (
                                <PreviewFallback>
                                    <span>
                                        {isLargeFile ? (
                                            <>
                                                <strong>{lnT('folders:largeFile')}</strong>{lnT('messages:previewNotAvailable')}</>
                                        ) : (
                                            <>
                                                <strong>{lnT('messages:heic')}</strong>{lnT('messages:imagesCanBeDisplayedAfterUpload')}</>
                                        )}
                                    </span>
                                    <span style={{ marginTop: 8, color: 'rgba(255,255,255,0.6)' }}>{decodeDisplayText(name)}</span>
                                </PreviewFallback>
                            )}
                        </ImageStage>
                        {hasPreview && renderCarousel({ src: reader.result })}
                    </ImageWrapper>
                );
            });
        }
    };

    const renderCarousel = item => {
        // For carousel, pass the full image object so CarouselModal can use thumbnail.xl
        const carouselItem = message.image || item;
        return (
            <CarouselModal
                readOnly={readOnly}
                allowExtraction={false}
                isOpen={lightboxIsOpen}
                closeModal={e => {
                    e.stopPropagation();
                    setLightboxIsOpen(false);
                }}
                items={[carouselItem]}
                fileType="Image"
                currentMessage={message}
                messageId={message?.id}
            ></CarouselModal>
        );
    };

    const renderImageMenuTrigger = menuChildren => {
        return (
            <ImageMenuContainer
                ref={actionMenuRef}
                onClick={event => {
                    event.stopPropagation();
                }}
            >
                <ImageMenuTrigger
                    type="button"
                    aria-label={lnT('messages:imageActions')}
                    onClick={event => {
                        event.stopPropagation();
                        setShowImageActionMenu(previous => !previous);
                    }}
                >
                    <FiMoreVertical size={16} color={'rgba(255,255,255,0.8)'} />
                </ImageMenuTrigger>
                {showImageActionMenu ? <ImageMenu>{menuChildren}</ImageMenu> : null}
            </ImageMenuContainer>
        );
    };

    const renderImageInfoSection = infoRows => {
        const visibleRows = infoRows.filter(row => row?.value);
        if (!visibleRows.length) {
            return null;
        }

        return (
            <>
                <ImageMenuSeparator />
                <ImageMenuLabel>{lnT('messages:info')}</ImageMenuLabel>
                {visibleRows.map(row => (
                    <ImageMenuMetaRow key={row.label}>
                        <ImageMenuMetaLabel>{row.label}</ImageMenuMetaLabel>
                        <ImageMenuMetaValue>{row.value}</ImageMenuMetaValue>
                    </ImageMenuMetaRow>
                ))}
            </>
        );
    };

    if (imagesPreview) {
        return renderPreview();
    } else if (message.image) {
        const { url, public_id, size, name, displayName, cdn_url } = message.image;
        
        // Detect if this is a Cloudinary image
        // Cloudinary images have a public_id like "luckynote/abc123" - S3 images don't have one
        const isCloudinaryImage = isCloudinaryMedia(message.image);
        
        // For S3 images, prefer thumbnail.lg for MessagePreviewComponent (detail view)
        // Fallback chain: thumbnail.lg > thumbnail.md > cdn_url > url
        const imageUrl = isCloudinaryImage ? null : (
            message.image?.thumbnail?.lg || 
            message.image?.thumbnail?.md || 
            cdn_url || 
            url
        );
        
        return (
            <>
                <ImageWrapper ref={wrapperRef} style={{ ...style }}>
                    {!hideActions ? (
                        <TopSection>
                            <MessageColorPalette message={message} />
                            {!readOnly && message?.id && !String(message.id).startsWith('temp') && typeof onOpenVideoWorkspace === 'function' ? (
                                <SimilarColorImages message={message} onOpen={onOpenVideoWorkspace} />
                            ) : null}
                            <TopRightActions>
                                {resolvedOcrText ? (
                                    <OcrCopyButton type="button" onClick={copyOcrText} title={lnT('messages:copyImageText')} aria-label={lnT('messages:copyImageText')}>
                                        {ocrCopied ? <FiCheck size={16} /> : <LuScanText size={16} />}
                                    </OcrCopyButton>
                                ) : null}
                                {renderImageMenuTrigger(
                                    <>
                                        <ImageMenuItem
                                            onClick={e => {
                                                e.stopPropagation();
                                                closeImageActionMenu();
                                                downloadImage(url, displayName, size, message.image);
                                            }}
                                        >
                                            <ImageMenuIcon>
                                                <FiDownload size={16} />
                                            </ImageMenuIcon>{lnT('messages:download')}</ImageMenuItem>
                                        {resolvedOcrText ? (
                                            <ImageMenuItem
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    closeImageActionMenu();
                                                    copyOcrText(e);
                                                }}
                                            >
                                                <ImageMenuIcon>
                                                    {ocrCopied ? <FiCheck size={16} /> : <LuScanText size={16} />}
                                                </ImageMenuIcon>
                                                {ocrCopied ? lnT('messages:copied') : lnT('messages:copyImageText')}
                                            </ImageMenuItem>
                                        ) : null}
                                        {!readOnly ? <>
                                        <ImageMenuItem
                                            onClick={e => {
                                                e.stopPropagation();
                                                closeImageActionMenu();
                                                setIsRenameModalOpen(true);
                                            }}
                                        >
                                            <ImageMenuIcon>
                                                <FiEdit2 size={16} />
                                            </ImageMenuIcon>{lnT('folders:rename')}</ImageMenuItem>
                                        <ImageMenuItem
                                            onClick={e => {
                                                e.stopPropagation();
                                                closeImageActionMenu();
                                                onButtonClick();
                                            }}
                                        >
                                            <ImageMenuIcon>
                                                <IoIosRepeat size={16} />
                                            </ImageMenuIcon>
                                            {uploadProgress &&
                                            uploadProgress[message.id] &&
                                            uploadProgress[message.id].type === 'image'
                                                ? lnT('messages:updating', { value1: String(parseInt(uploadProgress[message.id].percentage)) })
                                                : lnT('messages:replace')}
                                        </ImageMenuItem>
                                        <ImageMenuSeparator />
                                        <ImageMenuItem
                                            onClick={e => {
                                                e.stopPropagation();
                                                closeImageActionMenu();
                                                setIsDeleteModalOpen(true);
                                            }}
                                        >
                                            <ImageMenuIcon destructive>
                                                <FiTrash2 size={16} />
                                            </ImageMenuIcon>
                                            <ImageMenuDestructiveLabel>{lnT('common:remove')}</ImageMenuDestructiveLabel>
                                        </ImageMenuItem>
                                        </> : null}
                                        {renderImageInfoSection([
                                            { label: lnT('common:name'), value: decodeDisplayText(displayName || name) },
                                            { label: lnT('folders:size'), value: size ? prettySize(size) : null },
                                            {
                                                label: lnT('messages:format'),
                                                value: message.image?.format ? String(message.image.format).toUpperCase() : null
                                            },
                                            {
                                                label: lnT('messages:dimensions'),
                                                value:
                                                    message.image?.width && message.image?.height
                                                        ? `${message.image.width} x ${message.image.height}`
                                                        : null
                                            }
                                        ])}
                                    </>
                                )}
                            </TopRightActions>
                        </TopSection>
                    ) : null}
                    <ImageStage>
                        {isCloudinaryImage ? (
                            // Cloudinary image - use Cloudinary component
                            <Image
                                cloudName={environment.REACT_APP_CLOUD_NAME}
                                publicId={public_id ? public_id : url}
                                width={width ? width : wrapperWidth}
                                height={height ? height : 300}
                                crop={crop ? crop : 'fit'}
                                style={{
                                    cursor: 'zoom-in',
                                    maxWidth: '100%',
                                    maxHeight: height ? height : 300,
                                    width: 'auto',
                                    height: 'auto',
                                    borderRadius: 12
                                }}
                                onClick={e => {
                                    e.stopPropagation();
                                    openSavedMedia();
                                }}
                                {...rest}
                            >
                                <Transformation fetchFormat="auto" flags="lossy" quality="50" />
                            </Image>
                        ) : (
                            // S3 image - use CDN URL with fallback to S3 signed URL
                            <RecoverableImage as={PreviewImage}
                                sources={mediaImageSources(message.image)} refresh={messageImageRefresh(message.id)} allowRefresh={!readOnly}
                                src={imageUrl}
                                alt={displayName || name || lnT('messages:image')}
                                style={{
                                    cursor: 'zoom-in',
                                    maxHeight: height ? height : 300,
                                    objectFit: crop ? crop : 'contain'
                                }}
                                onClick={e => {
                                    e.stopPropagation();
                                    openSavedMedia();
                                }}
                                {...rest}
                            />
                        )}
                    </ImageStage>
                    {hideActions ? <MessageColorPalette message={message} /> : null}
                    {!hideActions ? (
                        <>
                            <DeleteModal
                                title={lnT('messages:deleteImage_6aee1566')}
                                description={lnT('messages:youWillNotBeAbleToGet')}
                                isOpen={isDeleteModalOpen}
                                closeModal={response => {
                                    if (response) {
                                        deleteImage();
                                    }
                                    setIsDeleteModalOpen(false);
                                }}
                            ></DeleteModal>
                            <RenameFileModal
                                isOpen={isRenameModalOpen}
                                media={message.image}
                                mediaLabel={lnT('messages:image_6105d6cc')}
                                onClose={() => setIsRenameModalOpen(false)}
                                onSubmit={renameImage}
                            />
                        </>
                    ) : null}
                    <input
                        type="file"
                        accept="image/*, .heic"
                        id="file"
                        ref={inputFile}
                        style={{ display: 'none' }}
                        onChange={onChangeFile}
                    />
                    {hideActions && resolvedOcrText ? (
                        <OcrCopyButton type="button" onClick={copyOcrText} title={lnT('messages:copyImageText')} aria-label={lnT('messages:copyImageText')}>
                            {ocrCopied ? <FiCheck size={15} color="#fff" /> : <LuScanText size={15} color="#fff" />}
                        </OcrCopyButton>
                    ) : null}
                </ImageWrapper>
                {renderCarousel(message.image)} 
            </>
        );
    } else {
        if (readOnly || hideAddButton) {
            return null;
        }
        return (
            <AddFileWrapper
                style={{ ...style }}
                uploading={uploadProgress && uploadProgress[message.id] && uploadProgress[message.id].type === 'image'}
                onClick={() => onButtonClick()}
            >
                <div style={{ color: 'white' }}>
                    {uploadProgress && uploadProgress[message.id] && uploadProgress[message.id].type === 'image'
                        ? lnT('messages:uploading_72cb29c9')
                        : lnT('messages:attach')}
                </div>

                <input
                    type="file"
                    accept="image/*"
                    id="file"
                    ref={inputFile}
                    style={{ display: 'none' }}
                    onChange={onChangeFile}
                />
            </AddFileWrapper>
        );
    }
}

const AddFileWrapper = styled.div`
    border-radius: 12px;
    border: 1px solid ${PALE_WHITE_1};
    background-color: ${props => (props.uploading ? GREEN : 'rgba(255, 255, 255, 0.04)')};
    cursor: pointer;
    padding: 10px 14px;
    display: flex;
    justify-content: center;
    &:hover {
        background-color: ${props => (props.uploading ? GREEN : 'rgba(255, 255, 255, 0.1)')};
    }
`;

const TopRightActions = styled.div`
    margin-left: auto;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
`;

const ImageStage = styled.div`
    width: 100%;
    min-height: 300px;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    border-radius: 12px;
`;

const PreviewImage = styled.img`
    border-radius: 12px;
    display: block;
    max-width: 100%;
    max-height: 100%;
`;

const PreviewFallback = styled.div`
    justify-content: center;
    align-items: center;
    display: flex;
    margin: 0 40px;
    height: 200px;
    flex-direction: column;
`;

const TopSection = styled.div`
    order: 1;
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    gap: 0 12px;
    min-height: 50px;
    z-index: 20;
`;

const OcrCopyButton = styled.button`
    align-self: flex-end;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    padding: 0;
    &:hover {
        color: ${WHITE};
        background-color: ${PALE_WHITE_1};
    }
    &:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }
`;

const ImageWrapper = styled.div`
    position: relative;
    width: 100%;
    max-width: 100%;
    margin-inline: auto;
    border-radius: 12px;
    min-height: 300px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    overflow: visible;
`;

const ImageMenuContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const ImageMenuTrigger = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
    color: ${PALE_WHITE_6};
    &:hover {
        color: ${WHITE};
        background-color: ${PALE_WHITE_1};
    }
    &:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }
`;

const ImageMenu = styled.div`
    position: absolute;
    bottom: calc(100% + 8px);
    right: 0;
    min-width: 200px;
    ${standardMenuSurface}
    color: rgba(255, 255, 255, 0.92);
    z-index: 5000;
`;

const ImageMenuItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    cursor: ${props => (props.$disabled ? 'default' : 'pointer')};
    user-select: none;
    opacity: ${props => (props.$disabled ? 0.62 : 1)};

    &:hover {
        background-color: ${props => (props.$disabled ? 'transparent' : 'rgba(255, 255, 255, 0.08)')};
    }
`;

const ImageMenuSeparator = styled.div`
    height: 1px;
    background-color: rgba(255, 255, 255, 0.1);
    margin: 2px 0;
`;

const ImageMenuIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${props => (props.destructive ? '#ff7a7a' : 'rgba(255, 255, 255, 0.78)')};
`;

const ImageMenuDestructiveLabel = styled.span`
    color: #ff7a7a;
`;

const ImageMenuLabel = styled.div`
    padding: 8px 12px 4px;
    font-size: 11px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.58);
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const ImageMenuMetaRow = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 12px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.82);
`;

const ImageMenuMetaLabel = styled.span`
    color: rgba(255, 255, 255, 0.58);
    flex: 0 0 auto;
`;

const ImageMenuMetaValue = styled.span`
    text-align: right;
    overflow-wrap: anywhere;
`;

export default ImagePreviewPlugin;
